import type { BlockTableData, TableDiff } from '../types';

/**
 * This function checks if the visibility of the table header or card title has changed
 * and stores the changes in the diff object passed to it.
 */
export function diffTableHeaderOrTitleVisibility(
    beforeData: BlockTableData,
    afterData: BlockTableData,
    diff: TableDiff
): void {
    // If the table header visibility has changed, we need to store it on the diff.
    if (
        'hideHeader' in beforeData.view &&
        'hideHeader' in afterData.view &&
        beforeData.view.hideHeader !== afterData.view.hideHeader
    ) {
        diff.view.hideHeader = {
            before: beforeData.view.hideHeader,
            after: afterData.view.hideHeader,
        };
    }

    // If the card title visibility has changed, we need to store it on the diff.
    if ('hideColumnTitle' in beforeData.view && 'hideColumnTitle' in afterData.view) {
        const prevHideColumnTitle = beforeData.view.hideColumnTitle ?? false;
        const hideColumnTitle = afterData.view.hideColumnTitle ?? false;
        if (prevHideColumnTitle !== hideColumnTitle) {
            diff.view.hideColumnTitle = {
                before: prevHideColumnTitle,
                after: hideColumnTitle,
            };
        }
    }
}

/**
 * This function compares the definitions of a table before and after an edit,
 * and stores any changes in the diff object passed to it.
 */
export function diffTableDefinitions(
    beforeData: BlockTableData,
    afterData: BlockTableData,
    diff: TableDiff
): TableDiff {
    Object.entries(afterData.definition).forEach(([key, afterDefinition]) => {
        const beforeDefinition = beforeData.definition[key];
        if (beforeDefinition) {
            // If the definition has changed, we need to store it on the diff.
            const titleChanged = beforeDefinition.title !== afterDefinition.title;
            const typeChanged = beforeDefinition.type !== afterDefinition.type;
            const textAlignmentChanged =
                beforeDefinition.type === 'text' &&
                afterDefinition.type === 'text' &&
                beforeDefinition.textAlignment !== afterDefinition.textAlignment;
            const verticalAlignmentChanged =
                beforeDefinition.type === 'text' &&
                afterDefinition.type === 'text' &&
                beforeDefinition.verticalAlignment !== afterDefinition.verticalAlignment;
            const isDefinitionChanged =
                titleChanged || typeChanged || textAlignmentChanged || verticalAlignmentChanged;

            if (isDefinitionChanged) {
                diff.definitions.set(key, {
                    before: beforeDefinition,
                    after: afterDefinition,
                });
            }
        } else {
            // If the definition is new, we need to store it on the diff.
            diff.definitions.set(key, {
                before: undefined,
                after: afterDefinition,
            });
        }
    });

    return diff;
}
